import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Icon } from "@iconify/react";
import { useAuth } from "../../Context/authContext";
import { Typography } from "../../Components/Typography";
import { getAllUsers } from "../../Utils/apiServices";
import Avatar from "../../Components/Avatar";

const getDateString = () => {
  return new Date().toLocaleDateString(undefined, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const AdminDashboard = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [stats, setStats] = useState([
    { label: "Students", value: 0, role: "student" },
    { label: "Mentors", value: 0, role: "mentor" },
    { label: "Sponsors", value: 0, role: "sponsor" },
    { label: "Partners", value: 0, role: "partner" },
    { label: "Donors", value: 0, role: "donor" },
    { label: "Investors", value: 0, role: "investor" },
    { label: "Volunteers", value: 0, role: "volunteer" },
  ]);
  const [loading, setLoading] = useState(true);

  const fetchUserCounts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getAllUsers();
      
      console.log("AdminDashboard API Response:", response);
      console.log("Response type:", typeof response);
      console.log("Response.success:", response?.success);
      console.log("Response.data:", response?.data);
      
      // Handle different response structures
      let users = [];
      
      // Check if response exists and is successful
      if (response && (response.success === true || response.success === undefined)) {
        // Try response.data.data (most common structure - API returns { data: [...] })
        if (Array.isArray(response.data?.data)) {
          users = response.data.data;
          console.log("Found users in response.data.data");
        }
        // Try response.data directly if it's an array
        else if (Array.isArray(response.data)) {
          users = response.data;
          console.log("Found users in response.data");
        }
        // Try response.data.data.users
        else if (response.data?.data?.users && Array.isArray(response.data.data.users)) {
          users = response.data.data.users;
          console.log("Found users in response.data.data.users");
        }
        // Try nested object structures
        else if (response.data?.data && typeof response.data.data === 'object') {
          // Check if data itself is an array
          if (Array.isArray(response.data.data)) {
            users = response.data.data;
            console.log("Found users in response.data.data (array check)");
          }
          // Try to find users array in object
          else if (response.data.data.users && Array.isArray(response.data.data.users)) {
            users = response.data.data.users;
            console.log("Found users in response.data.data.users (object check)");
          }
          // Try to extract any array from object values
          else {
            const arrays = Object.values(response.data.data).filter(Array.isArray);
            if (arrays.length > 0) {
              users = arrays[0];
              console.log("Found users in object values");
            }
          }
        }
        // Try response directly if it's an array
        else if (Array.isArray(response)) {
          users = response;
          console.log("Found users in response (direct array)");
        }
      } else {
        console.warn("Response not successful or missing:", response);
      }
      
      console.log("Extracted users:", users);
      console.log("Users count:", users.length);
      
      // Count users by role (Partners = ecosystem_partner + corporate_sponsor only)
      const roleCounts = {
        student: 0,
        mentor: 0,
        sponsor: 0,
        ecosystem_partner: 0,
        corporate_sponsor: 0,
        donor: 0,
        investor: 0,
        volunteer: 0,
      };

      if (users && users.length > 0) {
        users.forEach((user) => {
          const role = user.role?.toLowerCase()?.trim();
          if (role && roleCounts.hasOwnProperty(role)) {
            roleCounts[role]++;
          } else if (role) {
            console.log(`Unknown role found: "${role}"`);
          }
        });
      } else {
        console.warn("No users found in response!");
      }

      const partnersTotal = (roleCounts.ecosystem_partner || 0) + (roleCounts.corporate_sponsor || 0);
      console.log("Role counts:", roleCounts);
      console.log("Updating stats with:", roleCounts);

      // Always update stats, even if counts are 0
      const newStats = [
        { label: "Students", value: roleCounts.student, role: "student" },
        { label: "Mentors", value: roleCounts.mentor, role: "mentor" },
        { label: "Sponsors", value: roleCounts.sponsor, role: "sponsor" },
        { label: "Partners", value: partnersTotal, role: "partner" },
        { label: "Donors", value: roleCounts.donor, role: "donor" },
        { label: "Investors", value: roleCounts.investor, role: "investor" },
        { label: "Volunteers", value: roleCounts.volunteer, role: "volunteer" },
      ];
      
      console.log("New stats to set:", newStats);
      setStats(newStats);
    } catch (error) {
      console.error("Error fetching user counts:", error);
      console.error("Error details:", error.response || error.message);
      // Keep the stats at 0 if there's an error, but don't show loading state
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUserCounts();
  }, [fetchUserCounts, location.pathname]);

  const getDisplayName = () => {
    if (user?.first_name || user?.last_name) {
      return `${user?.first_name || ""} ${user?.last_name || ""}`.trim() || user?.email || "Guest";
    }
    return user?.email || "Guest";
  };

  const getRoleIcon = (role) => {
    const iconMap = {
      student: "lucide:graduation-cap",
      mentor: "lucide:user-check",
      sponsor: "lucide:handshake",
      partner: "lucide:users",
      donor: "lucide:heart-handshake",
      investor: "lucide:trending-up",
      volunteer: "lucide:hand-heart",
    };
    return iconMap[role] || "lucide:user";
  };

  return (
    <section className="flex flex-col w-full h-auto rounded-[40px] border-tl pt-8 pb-12 gap-8 bg-primary">
      <div className="flex w-auto h-auto px-8 items-start justify-start gap-4">
        <Avatar
          src={user?.profile_picture_url}
          firstName={user?.first_name}
          lastName={user?.last_name}
          email={user?.email}
          alt="User avatar"
          size="w-16 h-16"
          showBorder={true}
          className="border-slate-200"
        />
        <div className="flex flex-col gap-1 items-start justify-start">
          <p className="text-xl font-semibold text-primary-900">
            Hello, {getDisplayName()}
          </p>
          <p className="text-sm text-tertiary-600">{getDateString()}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center px-8 py-12">
          <Typography as="p" className="text-tertiary-600">
            Loading...
          </Typography>
        </div>
      ) : (
        <div className="flex w-auto h-auto px-8 items-start justify-start gap-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {stats.map((item) => (
            <div
              key={item.label}
              className="bg-primary border border-slate-200 rounded-2xl shadow-sm p-6 flex flex-col gap-3"
            >
              <div className="h-12 w-12 rounded-full bg-tertiary flex items-center justify-center">
                <Icon
                  icon={getRoleIcon(item.role)}
                  className="h-6 w-6 text-gray-500"
                />
              </div>
              <div className="flex flex-col gap-1 items-start justify-start">
                <Typography as="p" className="text-sm text-tertiary-600">
                  {item.label}
                </Typography>
                <Typography as="h3" className="text-3xl font-bold text-primary-900">
                  {item.value}
                </Typography>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default AdminDashboard;
